import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';

/**
 * Round 145 (owner ruling 2026-10-25: every page has a light / dark switch) - the language and theme switches as ONE cluster, and the rule that
 * makes "every screen has them" structural instead of a promise each page has to keep:
 *   - <PageControls /> is the cluster a page shows in its own header (TopBar renders it; Choose specialty and the specialty page too).
 *     LanguageToggle hides itself where there is no LanguageProvider (the admin site), so admin shows the theme switch only.
 *   - <FloatingControls level="learner" /> (in LearnerLayout) puts the same cluster in the top corner of any learner screen that shows no
 *     <PageControls /> of its own: welcome, sign-in, sign-up, forgot password, /auth/action, confirm your email, the profile step, payment result...
 *   - <FloatingControls level="root" /> (in App, outside every router and error boundary) shows the theme switch when nothing else does: the
 *     admin sign-in and two-step screens, the error screen, the pending-deletion screen, the loading screen.
 * Claims are taken in a layout effect, so the corner cluster never paints for a frame next to a page's own one.
 * frontend/src/components/ui/PageControls.test.tsx and the route test fail if a screen can render without a switch.
 */
type Kind = 'inline' | 'floating';
interface ControlsState { inline: number; floating: number; claim: (kind: Kind) => () => void }

const ControlsContext = createContext<ControlsState | null>(null);

export function ControlsProvider({ children }: { children: ReactNode }) {
  const [counts, setCounts] = useState({ inline: 0, floating: 0 });
  const claim = useCallback((kind: Kind) => {
    setCounts((c) => ({ ...c, [kind]: c[kind] + 1 }));
    return () => setCounts((c) => ({ ...c, [kind]: c[kind] - 1 }));
  }, []);
  const value = useMemo(() => ({ ...counts, claim }), [counts, claim]);
  return <ControlsContext.Provider value={value}>{children}</ControlsContext.Provider>;
}

function useClaim(kind: Kind) {
  const claim = useContext(ControlsContext)?.claim;
  useLayoutEffect(() => (claim ? claim(kind) : undefined), [claim, kind]);
}

/** The cluster a page shows in its own header. */
export default function PageControls({ className }: { className?: string }) {
  useClaim('inline');
  return (
    <div className={clsx('flex items-center gap-1 shrink-0', className)} data-testid="page-controls">
      <LanguageToggle />
      <ThemeToggle />
    </div>
  );
}

/**
 * The learner fallback claims "floating" so the root one stays hidden; the root one claims nothing (it would hide itself).
 * It is a ROW at the top of the page, not a floating box: floating, it covered the heading of pages whose heading starts at the very top (the
 * sign-up form at 360 px, found by the Round 145 browser run). body.has-corner-controls shortens full-height pages by the row (index.css), so
 * a centred page does not grow a scrollbar.
 */
function LearnerFloating() {
  useClaim('floating');
  useEffect(() => {
    document.body.classList.add('has-corner-controls');
    return () => document.body.classList.remove('has-corner-controls');
  }, []);
  return (
    <div className="corner-controls-row flex justify-end items-center px-2" data-testid="corner-controls-row">
      <Floating withLanguage inRow />
    </div>
  );
}

function Floating({ withLanguage, inRow = false }: { withLanguage: boolean; inRow?: boolean }) {
  return (
    <div
      className={clsx(inRow ? 'relative' : 'absolute top-2 end-2', 'z-[55] flex items-center gap-1 rounded-xl border border-line bg-card shadow-sm')}
      data-testid="floating-controls"
    >
      {withLanguage && <LanguageToggle />}
      <ThemeToggle />
    </div>
  );
}

/** The fallback: shown only when no <PageControls /> is on screen (and, for "root", no learner fallback either). */
export function FloatingControls({ level }: { level: 'learner' | 'root' }) {
  const ctx = useContext(ControlsContext);
  const show = !ctx || (ctx.inline === 0 && (level === 'learner' || ctx.floating === 0));
  if (!show) return null;
  return level === 'learner' ? <LearnerFloating /> : <Floating withLanguage={false} />;
}
